import axios from 'axios';
import Cookies from 'js-cookie';
import { getApiBaseUrl } from '../config/apiConfig';

/**
 * Global Observability Helper
 * Standardizes error reporting across the React web app.
 */

const LOG_ENDPOINT = '/api/v2/log-client-error';

// Capture build/deployment info if available
const BUILD_HASH = process.env.REACT_APP_BUILD_HASH || 'dev-' + new Date().toISOString().split('T')[0];

/**
 * Reports a client-side error to the central logging service.
 */
export const reportError = async (data) => {
    try {
        const apiBase = getApiBaseUrl();
        const payload = {
            source: data.source || 'web_app',
            stage: data.stage || 'runtime',
            message: data.message || 'Unknown error',
            stack: data.stack || new Error().stack,
            user_id: data.user_id || Cookies.get('userId') || 'anonymous',
            event_id: data.event_id || '',
            platform: 'web',
            build_hash: BUILD_HASH,
            hostname: window.location.hostname,
            context: {
                url: window.location.href,
                userAgent: navigator.userAgent,
                ...data.context
            }
        };

        // Fire and forget (best effort)
        axios.post(`${apiBase}${LOG_ENDPOINT}`, payload).catch(() => {
            // Silently fail to avoid recursive error loops
        });
    } catch (e) {
        // Fallback to console if everything fails
        console.error('Failed to report error:', e);
    }
};

/**
 * Initializes global HTTP interceptors to catch and report all non-200 responses.
 */
export const initObservability = () => {
    let isSessionExpiredAlertShown = false;

    // Intercept responses
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error.response ? error.response.status : null;

            // Handle Maintenance (503 Service Unavailable) - Do not redirect admin portal
            if (status === 503) {
                return Promise.reject(error);
            }

            // Handle Session Expiry (401/403)
            if ((status === 401 || status === 403) && !isSessionExpiredAlertShown) {
                isSessionExpiredAlertShown = true;
                alert("Session Expired: Your session has become invalid. Please log in again to continue.");
                // Redirect to login (assuming root is login or has redirect logic)
                window.location.href = "/";
                return Promise.reject(error);
            }

            // Log only if it's a real failure (not handled above)
            if (status >= 400 && status !== 401 && status !== 403) {
                reportError({
                    source: 'axios_interceptor',
                    stage: 'network',
                    message: `HTTP ${error.response.status}: ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
                    context: {
                        status: error.response.status,
                        data: error.response.data,
                        params: error.config?.params,
                        headers: error.config?.headers
                    }
                });
            } else if (!error.response) {
                // Network error (no response)
                reportError({
                    source: 'axios_interceptor',
                    stage: 'network_offline',
                    message: `Network Error: ${error.message}`,
                    context: {
                        config: error.config
                    }
                });
            }
            return Promise.reject(error);
        }
    );

    // Global uncaught error handler
    window.onerror = (message, source, lineno, colno, error) => {
        reportError({
            source: 'window_onerror',
            stage: 'panic',
            message: message,
            stack: error?.stack,
            context: { source, lineno, colno }
        });
    };

    // Unhandled promise rejection
    window.onunhandledrejection = (event) => {
        reportError({
            source: 'unhandled_rejection',
            stage: 'async_panic',
            message: event.reason?.message || 'Unhandled Promise Rejection',
            stack: event.reason?.stack,
            context: { reason: event.reason }
        });
    };

    console.log('[Observability] Global error interceptors initialized.');
};
